import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import styled from '@/styles';
import { GetJobQuery } from '@/types/graphql';
import { axiosRubyClient } from '@/util/axios/axiosClient';

interface ShareQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare: () => void;
  job: GetJobQuery['job'];
}

const HeaderDialog = styled('div', {
  padding: '20px 25px 15px 25px',
  background: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #E7E8EA',
});

const TitleDialog = styled('h2', {
  color: '#0E1E40',
  fontSize: '24px',
  fontWeight: '600',
  margin: 0,
});

const ModalContent = styled('div', {
  padding: '30px 25px 40px',
  background: 'white',
});

const Description = styled('p', {
  color: '#666',
  fontSize: '16px',
  lineHeight: '1.5',
  margin: '0 0 30px 0',
});

const SectionTitle = styled('h3', {
  color: '#0E1E40',
  fontSize: '16px',
  fontWeight: '600',
  margin: '0 0 15px 0',
});

const InputField = styled('input', {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #E7E8EA',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.2s ease',

  '&:focus': {
    borderColor: '#52C241',
  },

  '&::placeholder': {
    color: '#999',
  },

  '&.error': {
    borderColor: '#E53E3E',
  },
});

const ErrorText = styled('span', {
  display: 'block',
  color: '#E53E3E',
  fontSize: '12px',
  marginTop: '6px',
  fontWeight: '500',
});

const SuccessMessage = styled('div', {
  textAlign: 'center',
  padding: '40px 20px',
});

const CheckIcon = styled('svg', {
  width: '64px',
  height: '64px',
  fill: '#52C241',
  marginBottom: '16px',
});

const SuccessText = styled('p', {
  color: '#0E1E40',
  fontSize: '18px',
  fontWeight: '600',
  margin: 0,
});

const ShortlinkContainer = styled('div', {
  marginTop: '20px',
  padding: '16px',
  background: '#F7F9FC',
  borderRadius: '8px',
  border: '1px solid #E7E8EA',
});

const ShortlinkLabel = styled('p', {
  color: '#666',
  fontSize: '14px',
  fontWeight: '500',
  margin: '0 0 8px 0',
});

const ShortlinkText = styled('a', {
  color: '#52C241',
  fontSize: '14px',
  fontWeight: '600',
  wordBreak: 'break-all',
  textDecoration: 'none',
  display: 'block',

  '&:hover': {
    textDecoration: 'underline',
  },
});

const ButtonContainer = styled('div', {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  marginTop: '30px',
});

const CancelButton = styled('button', {
  padding: '12px 24px',
  border: '1px solid #E7E8EA',
  borderRadius: '8px',
  background: 'white',
  color: '#666',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    borderColor: '#ccc',
    background: '#f9f9f9',
  },
});

const ShareButton = styled('button', {
  padding: '12px 24px',
  border: '1px solid #52C241',
  borderRadius: '8px',
  background: '#52C241',
  color: 'white',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    background: '#45A735',
    borderColor: '#45A735',
  },

  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
});

const ShareQRCodeModal: React.FC<ShareQRCodeModalProps> = ({
  isOpen,
  onClose,
  onShare,
  job,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Access shortlink property (added to backend but not yet in GraphQL types)
  const shortlink = (job as any).shortlink as string | undefined;

  // Validate US phone number (10 digits)
  const validateUSPhoneNumber = (phone: string): boolean => {
    const cleaned = phone.replace(/\D/g, '');
    // Must be exactly 10 digits (US number without country code)
    return cleaned.length === 10;
  };

  // Format phone number as user types: (555) 555-1234
  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    let formatted = '';
    if (match[1]) formatted = `(${match[1]}`;
    if (match[2]) formatted += `) ${match[2]}`;
    if (match[3]) formatted += `-${match[3]}`;

    return formatted;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setError('');
  };

  const handleShare = async () => {
    // Validate phone number
    if (!validateUSPhoneNumber(phoneNumber)) {
      setError('Please enter a valid 10-digit US phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axiosRubyClient.post(
        `/api/jobs/${job.id}/send_qr_sms`,
        { phone_number: phoneNumber },
      );

      if (response.data.success) {
        setSuccess(true);
        // Auto close after 2 seconds
        setTimeout(() => {
          onShare();
          onClose();
          // Reset state after modal closes
          setTimeout(() => {
            setSuccess(false);
            setPhoneNumber('');
            setError('');
          }, 300);
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || 'Failed to send SMS. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset state after animation completes
      setTimeout(() => {
        setPhoneNumber('');
        setError('');
        setSuccess(false);
      }, 300);
    }
  };

  const isShareDisabled = !phoneNumber.trim() || loading;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          margin: '20px',
        },
      }}
    >
      <HeaderDialog>
        <TitleDialog>Share QR Code</TitleDialog>
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <CloseOutlined />
        </IconButton>
      </HeaderDialog>

      <ModalContent>
        {success ? (
          <SuccessMessage>
            <CheckIcon viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </CheckIcon>
            <SuccessText>SMS sent successfully!</SuccessText>
          </SuccessMessage>
        ) : (
          <>
            <Description>
              Can't make it to your shift? Share your QR code with the
              supervisor assigned to cover this shift.
            </Description>

            {shortlink && (
              <ShortlinkContainer>
                <ShortlinkLabel>QR Code Download Link:</ShortlinkLabel>
                <ShortlinkText
                  href={shortlink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {shortlink}
                </ShortlinkText>
              </ShortlinkContainer>
            )}

            <SectionTitle>Share via SMS</SectionTitle>
            <InputField
              type="tel"
              placeholder="(555) 555-1234"
              value={phoneNumber}
              onChange={handlePhoneChange}
              maxLength={14}
              disabled={loading}
              className={error ? 'error' : ''}
            />
            {error && <ErrorText>{error}</ErrorText>}

            <ButtonContainer>
              <CancelButton onClick={handleClose} disabled={loading}>
                Cancel
              </CancelButton>
              <ShareButton onClick={handleShare} disabled={isShareDisabled}>
                {loading ? 'Sending...' : 'Send SMS'}
              </ShareButton>
            </ButtonContainer>
          </>
        )}
      </ModalContent>
    </Dialog>
  );
};

export default ShareQRCodeModal;
